import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Brain, Browser, CalendarDots, CaretRight, ChartBar, ChartLineUp, ChatCircleDots, GearSix,
  CheckSquareOffset, DotsThree, FileText, GitBranch, Moon, PaperPlaneTilt,
  SignOut, Sparkle, SquaresFour, Sun, TrendUp, UserPlus, UsersThree, Wallet, X,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { normalizeCustomPages } from '../lib/pageAccess';

const ICONS = {
  'creator-operations': UsersThree,
  'command-center': SquaresFour,
  scheduler: CalendarDots,
  tasks: CheckSquareOffset,
  chatters: ChatCircleDots,
  'sales-overview': ChartLineUp,
  review: ChartBar,
  pipeline: GitBranch,
  finance: Wallet,
  'content-os': Browser,
  brain: Brain,
  'ai-content': Sparkle,
  'reddit-dashboard': TrendUp,
  telegram: PaperPlaneTilt,
  documents: FileText,
  onboarding: UserPlus,
  'models-hub': UsersThree,
  settings: GearSix,
};

const PAGES = {
  'creator-operations': { path: '/creator-operations', label: 'Creator Operations' },
  'command-center': { path: '/command-center', label: 'Command Center' },
  scheduler: { path: '/reels-operations', label: 'Reels Operations' },
  tasks: { path: '/tasks', label: 'Tasks' },
  chatters: { path: '/chatters', label: 'Chatters' },
  'sales-overview': { path: '/sales-overview', label: 'Sales Overview' },
  review: { path: '/review', label: 'Sunday Review' },
  pipeline: { path: '/pipeline', label: 'Pipeline' },
  finance: { path: '/finance', label: 'Finance' },
  'content-os': { path: '/content-os', label: 'Content OS' },
  brain: { path: '/knowledge', label: 'Knowledge' },
  'ai-content': { path: '/ai-content', label: 'AI Generator' },
  'reddit-dashboard': { path: '/reddit-dashboard', label: 'Reddit Operations' },
  telegram: { path: '/telegram', label: 'Send Alert' },
  documents: { path: '/documents', label: 'Documents' },
  onboarding: { path: '/onboarding', label: 'Model Onboarding' },
  'models-hub': { path: '/models-hub', label: 'Models Hub' },
  settings: { path: '/settings', label: 'Settings' },
};

const roleConfig = {
  owner: {
    bar: [
      { key: 'command-center', label: 'Home' },
      { key: 'scheduler', label: 'Reels' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'sales-overview', label: 'Sales' },
    ],
    groups: [
      { label: 'Operations', keys: ['command-center', 'tasks', 'review', 'telegram', 'documents', 'finance'] },
      { label: 'Traffic', keys: ['scheduler', 'reddit-dashboard'] },
      { label: 'Sales', keys: ['sales-overview', 'pipeline'] },
      { label: 'Models', keys: ['onboarding', 'models-hub', 'creator-operations'] },
      { label: 'Tools', keys: ['content-os', 'brain', 'ai-content', 'settings'] },
    ],
  },
  va: {
    bar: [
      { key: 'scheduler', label: 'Reels' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'telegram', label: 'Alert' },
      { key: 'documents', label: 'Docs' },
    ],
    groups: [
      { label: 'Daily operations', keys: ['scheduler', 'tasks', 'telegram', 'documents'] },
      { label: 'Traffic', keys: ['reddit-dashboard'] },
      { label: 'Models', keys: ['models-hub', 'onboarding', 'creator-operations'] },
      { label: 'Tools', keys: ['content-os', 'brain', 'ai-content'] },
    ],
  },
  chatter: {
    bar: [
      { key: 'chatters', label: 'Chat' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'documents', label: 'Docs' },
    ],
    groups: [
      { label: 'Daily operations', keys: ['chatters', 'tasks', 'documents'] },
      { label: 'Knowledge', keys: ['brain'] },
    ],
  },
};

const PAGE_LABEL_TO_KEY = {
  'Command Center': 'command-center', Scheduler: 'scheduler', 'Reels Operations': 'scheduler',
  Tasks: 'tasks', 'Chatters CRM': 'chatters', 'Sunday Review': 'review', Pipeline: 'pipeline',
  'AI Generator': 'ai-content', 'Reddit Dashboard': 'reddit-dashboard', 'Reddit Operations': 'reddit-dashboard',
  'Send Alert': 'telegram', Documents: 'documents', 'Model Onboarding': 'onboarding',
  Settings: 'settings', Finance: 'finance', 'Models Hub': 'models-hub',
  'Content OS': 'content-os', 'CUZO Brain': 'brain', Knowledge: 'brain',
  'Sales Overview': 'sales-overview',
};

const OWNER_SECTION_DEFINITIONS = [
  { id: 'operations', label: 'Operations', keys: ['command-center', 'tasks', 'review', 'telegram', 'documents', 'finance'] },
  { id: 'traffic', label: 'Traffic', keys: ['scheduler', 'reddit-dashboard'] },
  { id: 'sales', label: 'Sales', keys: ['sales-overview', 'pipeline'] },
  { id: 'models', label: 'Models', keys: ['onboarding', 'models-hub', 'creator-operations'] },
  { id: 'tools', label: 'Tools', keys: ['content-os', 'brain', 'ai-content', 'settings'] },
];
const OWNER_NAVIGATION_KEYS = OWNER_SECTION_DEFINITIONS.flatMap(section => section.keys);

function readNavigationCustomization(role) {
  try {
    return JSON.parse(localStorage.getItem(`cuzo_sidebar_${role}`)) || {};
  } catch (_) {
    return {};
  }
}

export default function BottomNav() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const config = roleConfig[user?.role] || { bar: [], groups: [] };
  const normalizedPages = normalizeCustomPages(user?.custom_pages);
  const allowedKeys = normalizedPages
    ? normalizedPages.map(label => PAGE_LABEL_TO_KEY[label]).filter(Boolean)
    : null;
  const customization = useMemo(() => readNavigationCustomization(user?.role), [user?.role, drawerOpen]);
  const hidden = customization.hidden || [];
  const labels = customization.labels || {};
  const canUse = key => (!allowedKeys || allowedKeys.includes(key)) && !hidden.includes(key);
  const ownerDefaultSection = key => OWNER_SECTION_DEFINITIONS.find(section => section.keys.includes(key))?.id || 'tools';
  const ownerSectionFor = key => OWNER_SECTION_DEFINITIONS.some(section => section.id === customization.groups?.[key])
    ? customization.groups[key]
    : ownerDefaultSection(key);
  const orderOwnerSection = (sectionId, keys) => {
    const savedOrder = customization.groupOrders?.[sectionId] || [];
    const order = [...savedOrder, ...keys.filter(key => !savedOrder.includes(key))];
    return [...keys].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  };

  const configuredGroups = user?.role === 'owner'
    ? OWNER_SECTION_DEFINITIONS.map(section => ({
      ...section,
      keys: orderOwnerSection(section.id, OWNER_NAVIGATION_KEYS.filter(key => ownerSectionFor(key) === section.id)),
    }))
    : config.groups;
  const groups = configuredGroups
    .map(group => ({
      ...group,
      items: group.keys.filter(canUse).map(key => ({
        key,
        ...PAGES[key],
        label: labels[key] || PAGES[key]?.label,
        icon: ICONS[key],
      })).filter(item => item.path),
    }))
    .filter(group => group.items.length);

  if (user?.role === 'chatter' && allowedKeys?.includes('reddit-dashboard') && !hidden.includes('reddit-dashboard')) {
    groups.push({
      label: 'Traffic',
      items: [{
        key: 'reddit-dashboard',
        ...PAGES['reddit-dashboard'],
        label: labels['reddit-dashboard'] || PAGES['reddit-dashboard'].label,
        icon: ICONS['reddit-dashboard'],
      }],
    });
  }

  const barItems = config.bar.filter(item => canUse(item.key)).map(item => ({
    ...item,
    ...PAGES[item.key],
    label: item.label,
    icon: ICONS[item.key],
  }));

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const close = event => { if (event.key === 'Escape') setDrawerOpen(false); };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', close);
    };
  }, [drawerOpen]);

  const go = path => { setDrawerOpen(false); navigate(path); };
  const signOut = () => { logout(); navigate('/login'); setDrawerOpen(false); };
  const isActive = path => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const displayName = user?.name || user?.display_name || 'CUZO team';
  const initials = displayName.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <button
        className={`cuzo-mobile-backdrop${drawerOpen ? ' is-open' : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-label="Close navigation"
        tabIndex={drawerOpen ? 0 : -1}
      />
      <aside
        id="cuzo-mobile-navigation"
        className={`cuzo-mobile-drawer${drawerOpen ? ' is-open' : ''}`}
        aria-hidden={!drawerOpen}
        inert={drawerOpen ? undefined : ''}
      >
        <header className="cuzo-mobile-drawer__header">
          <div>
            <div className="cuzo-wordmark">CUZO</div>
            <div className="cuzo-wordmark-sub">In-house</div>
          </div>
          <button className="cuzo-mobile-drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close navigation">
            <X size={21} weight="bold" />
          </button>
        </header>

        <div className="cuzo-mobile-drawer__identity">
          <div className="cuzo-mobile-drawer__avatar">{initials}</div>
          <div><b>{displayName}</b><span>{user?.role}</span></div>
        </div>

        <nav className="cuzo-mobile-drawer__content" aria-label="All operations">
          {groups.map(group => (
            <section className="cuzo-mobile-drawer__group" key={group.label}>
              <div className="cuzo-mobile-drawer__section">{group.label}</div>
              <div className="cuzo-mobile-drawer__links">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button className={`cuzo-mobile-drawer__link${active ? ' is-active' : ''}`} key={item.path} onClick={() => go(item.path)} aria-current={active ? 'page' : undefined}>
                      <Icon size={20} weight={active ? 'fill' : 'regular'} />
                      <span>{item.label}</span>
                      <CaretRight size={14} weight="bold" />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </nav>

        <footer className="cuzo-mobile-drawer__actions">
          <button onClick={toggleTheme}>{theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}<span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span></button>
          <button onClick={signOut}><SignOut size={18} /><span>Sign out</span></button>
        </footer>
      </aside>

      <nav
        className="cuzo-bottom-nav"
        aria-label="Mobile navigation"
        style={{ gridTemplateColumns: `repeat(${barItems.length + 1}, minmax(0, 1fr))` }}
      >
        {barItems.map(item => {
          const Icon = item.icon;
          return (
            <NavLink key={item.path} to={item.path} className={({ isActive: active }) => `cuzo-bottom-nav__item${active ? ' is-active' : ''}`}>
              <Icon size={23} weight={isActive(item.path) ? 'fill' : 'regular'} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <button className={`cuzo-bottom-nav__item${drawerOpen ? ' is-active' : ''}`} onClick={() => setDrawerOpen(open => !open)} aria-expanded={drawerOpen} aria-controls="cuzo-mobile-navigation">
          <DotsThree size={25} weight="bold" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
